import { Link } from 'react-router-dom';
import { ROUTE_NAMES } from '@tsgecart/config';
import { formatPaise } from '../../../../lib/format.js';
import type { CartView } from '../types/cart.js';

export interface CartSummaryProps {
  cart: CartView;
}

/**
 * Subtotal only -- `subtotalPaise` is the only total field this endpoint
 * returns. Discount/tax/delivery-fee/grand-total are computed by
 * checkout-preview (a different endpoint, built by another agent), so
 * this component never invents those rows or sums lines itself.
 */
export function CartSummary({ cart }: CartSummaryProps) {
  const disabled = cart.items.length === 0 || cart.hasBlockingIssues;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4">
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">Subtotal ({cart.itemCount} item{cart.itemCount === 1 ? '' : 's'})</span>
        <span className="font-semibold text-gray-900">{formatPaise(cart.subtotalPaise)}</span>
      </div>
      <p className="mt-1 text-xs text-gray-400">Taxes, delivery fee, and discounts calculated at checkout.</p>

      {cart.hasBlockingIssues && (
        <p role="alert" className="mt-3 text-xs font-medium text-red-600">
          Resolve the issues above before checking out.
        </p>
      )}

      <Link
        to={ROUTE_NAMES.checkout}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : undefined}
        onClick={(event) => {
          if (disabled) event.preventDefault();
        }}
        className={`focus-visible:ring-brand mt-4 block w-full rounded-full px-4 py-2.5 text-center text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 ${
          disabled
            ? 'pointer-events-none cursor-not-allowed bg-gray-200 text-gray-400'
            : 'bg-gray-900 text-white hover:bg-gray-800'
        }`}
      >
        Proceed to checkout
      </Link>
    </div>
  );
}
